import React, { useState, useEffect } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { Lock, Loader2, Eye, EyeOff, CheckCircle2, ArrowLeft } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { formatApiErrorDetail } from "@/lib/apiError";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const validatePassword = (pw) => {
  if (pw.length < 8) return "min_length";
  if (!/[A-Z]/.test(pw)) return "uppercase";
  if (!/[a-z]/.test(pw)) return "lowercase";
  if (!/[0-9]/.test(pw)) return "digit";
  return null;
};

const ResetPassword = () => {
  const { lang } = useLanguage();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!token) {
      setError(lang === "fr"
        ? "Lien invalide ou manquant. Veuillez demander un nouveau lien."
        : "Invalid or missing link. Please request a new one.");
    }
  }, [token, lang]);

  // Redirect to login a few seconds after success
  useEffect(() => {
    if (!success) return;
    const t = setTimeout(() => navigate("/admin/login"), 3500);
    return () => clearTimeout(t);
  }, [success, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    const issue = validatePassword(password);
    if (issue) {
      const messages = {
        min_length: lang === "fr" ? "Au moins 8 caractères." : "At least 8 characters.",
        uppercase: lang === "fr" ? "Au moins une majuscule." : "At least one uppercase letter.",
        lowercase: lang === "fr" ? "Au moins une minuscule." : "At least one lowercase letter.",
        digit: lang === "fr" ? "Au moins un chiffre." : "At least one digit.",
      };
      setError(messages[issue]);
      return;
    }
    if (password !== confirm) {
      setError(lang === "fr" ? "Les mots de passe ne correspondent pas." : "Passwords don't match.");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: password });
      setSuccess(true);
    } catch (err) {
      setError(formatApiErrorDetail(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="reset-password-page" className="min-h-screen flex items-center justify-center bg-[#050505] px-6 py-12">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        className="w-full max-w-md"
      >
        <Link
          to="/admin/login"
          className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.3em] text-[#A19E9C] hover:text-[#C5A059] transition-colors mb-12"
        >
          <ArrowLeft className="w-3 h-3" strokeWidth={1.5} />
          {lang === "fr" ? "Retour à la connexion" : "Back to sign in"}
        </Link>

        <div className="mb-10">
          <p className="overline text-[#C5A059] mb-3">
            {lang === "fr" ? "Nouveau mot de passe" : "New password"}
          </p>
          <h1 className="text-3xl md:text-4xl tracking-tight font-serif text-[#F2EBE5]">
            {lang === "fr" ? "Définir un nouveau mot de passe" : "Set a new password"}
          </h1>
        </div>

        {success ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            data-testid="reset-success"
            className="border border-[#C5A059]/30 bg-[#0a0a0a] p-6 flex items-start gap-4"
          >
            <CheckCircle2 className="w-5 h-5 text-[#C5A059] mt-0.5 flex-shrink-0" strokeWidth={1.5} />
            <div>
              <p className="text-sm font-serif text-[#F2EBE5] mb-2">
                {lang === "fr" ? "Mot de passe réinitialisé" : "Password reset"}
              </p>
              <p className="text-[13px] leading-relaxed text-[#A19E9C]">
                {lang === "fr"
                  ? "Vous allez être redirigé vers la page de connexion."
                  : "You'll be redirected to the sign-in page."}
              </p>
            </div>
          </motion.div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label htmlFor="password" className="block text-[10px] uppercase tracking-[0.3em] text-[#A19E9C] mb-2">
                {lang === "fr" ? "Nouveau mot de passe" : "New password"}
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#C5A059]" strokeWidth={1.5} />
                <input
                  id="password"
                  type={show ? "text" : "password"}
                  required
                  autoComplete="new-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  data-testid="reset-password-input"
                  className="kw-input pl-10 pr-10"
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  onClick={() => setShow((s) => !s)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#A19E9C] hover:text-[#C5A059]"
                  tabIndex={-1}
                >
                  {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <div>
              <label htmlFor="confirm" className="block text-[10px] uppercase tracking-[0.3em] text-[#A19E9C] mb-2">
                {lang === "fr" ? "Confirmer" : "Confirm"}
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#C5A059]" strokeWidth={1.5} />
                <input
                  id="confirm"
                  type={show ? "text" : "password"}
                  required
                  autoComplete="new-password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  data-testid="reset-confirm-input"
                  className="kw-input pl-10"
                  placeholder="••••••••"
                />
              </div>
            </div>

            <p className="text-[11px] leading-relaxed text-[#A19E9C]">
              {lang === "fr"
                ? "Au moins 8 caractères avec une majuscule, une minuscule et un chiffre."
                : "At least 8 characters with one uppercase, one lowercase and one digit."}
            </p>

            {error && (
              <p data-testid="reset-error" className="text-sm text-red-400 border-l-2 border-red-400/40 pl-3">
                {error}
              </p>
            )}

            <button
              type="submit"
              disabled={loading || !token || !password || !confirm}
              data-testid="reset-submit"
              className="kw-btn-primary w-full justify-center disabled:opacity-50"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.5} />
                  {lang === "fr" ? "Envoi…" : "Saving…"}
                </>
              ) : (
                <>{lang === "fr" ? "Réinitialiser" : "Reset password"}</>
              )}
            </button>
          </form>
        )}
      </motion.div>
    </div>
  );
};

export default ResetPassword;
